interface Location {
  address?: string;
  name?: string;
}

interface Contact {
  name?: string;
}

interface Product {
  temperature_range?: string;
}

interface AlertTrigger {
  currentValue?: string | number | null;
  thresholdValue?: string | number;
  timestamp?: string;
}

interface AlertContext {
  flight_number?: string;
  source_airport_code?: string;
  scheduled_on?: string;
  estimated_off?: string;
  airport_code?: string;
  cargo_closing_hours?: string;
  weather_event_severity?: string;
  overdue_duration?: string;
  remaining_viability_window?: string;
  estimated_transit_time_remaining?: string;
}

interface Alert {
  title?: string;
  detail?: string;
  trigger?: AlertTrigger;
  context?: AlertContext;
  primary_contact?: Contact;
}

interface Shipment {
  courier_tracking_id?: string;
  reference_number?: string;
  from_location?: Location;
  to_location?: Location;
  actual_pickup_date?: string;
  actual_pickup_date_formatted?: string;
  scheduled_pickup_date?: string;
  scheduled_pickup_date_formatted?: string;
  estimated_delivery_date?: string;
  estimated_delivery_date_formatted?: string;
  transport_provider_eta?: string;
  transport_provider_eta_formatted?: string;
  scheduled_delivery_date?: string;
  scheduled_delivery_date_formatted?: string;
  product?: Product;
  tp_primary_contact?: Contact;
}

interface DecodedToken {
  company_name?: string;
  user_role?: string;
}

interface EmailParts {
  subject: string;
  body: string;
  footer: string;
}

/**
 * Formats shipment information into a multi-line text string
 *
 * @param shipment - Shipment information to format
 * @returns Formatted shipment details with tracking ID, origin, and destination
 */
const getShipmentDetails = (shipment: Shipment | undefined): string => {
  if (!shipment) return '';

  const details = ['Shipment Details:'];
  if (shipment.courier_tracking_id) {
    details.push(`- Tracking number: ${shipment.courier_tracking_id}`);
  }
  if (shipment.reference_number) {
    details.push(`- Reference number: ${shipment.reference_number}`);
  }

  if (shipment?.from_location?.address) {
    let from = `- From: ${shipment.from_location.address}`;
    if (
      shipment.actual_pickup_date_formatted ||
      shipment.scheduled_pickup_date_formatted ||
      shipment.actual_pickup_date ||
      shipment.scheduled_pickup_date
    ) {
      from += ` (${shipment.actual_pickup_date_formatted || shipment.scheduled_pickup_date_formatted || shipment.actual_pickup_date || shipment.scheduled_pickup_date})`;
    }
    details.push(from);
  }

  if (shipment.to_location?.address) {
    let to = `- To: ${shipment.to_location.address}`;
    to += ` (${shipment?.estimated_delivery_date_formatted || shipment?.transport_provider_eta_formatted || shipment?.scheduled_delivery_date_formatted || shipment?.estimated_delivery_date || shipment?.transport_provider_eta || shipment?.scheduled_delivery_date || ''})`;
    details.push(to);
  }

  return details.join('\n');
};

const emailTemplate = {
  subject: `Alert [Shipment {{courier_tracking_id}}] - {{alert_name}}`,
  body: `Hi {{transport_provider.primary_contact_name}},

{{alertDetail}}

Kindly reply to this email with the action you've taken.`,

  footer: `Thanks,  
{{sender_name}}, {{sender_company}} 
Logistics team

{{shipment_details}}`,
};

/**
 * Generates email subject and body from alert and shipment data
 *
 * @param alert - Alert information with title and details
 * @param shipment - Shipment information for template replacement
 * @param decodedToken - User token containing company information
 * @returns Email subject and body with replaced placeholders
 */
export const generateEmailParts = ({
  alert,
  shipment,
  decodedToken,
}: {
  alert?: Alert;
  shipment?: Shipment;
  decodedToken?: DecodedToken;
}): EmailParts => {
  const alertName = alert?.title
    ? alert.title
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  // Find the template key case-insensitively
  //   const templateKey = Object.keys(templates).find(
  //     (key) => key.toLowerCase() === alertName?.toLowerCase()
  //   );

  if (!alert?.detail) {
    return {
      subject: `Alert: ${alertName} – Immediate Attention Required`,
      body: `Hi,

An alert "${alertName}" has been triggered for shipment ${
        shipment?.courier_tracking_id
      }.

Please investigate.`,

      footer: `Thanks,
${decodedToken?.company_name} Support`,
    };
  }
  let { body, subject, footer } = emailTemplate;
  const shipmentDetails = getShipmentDetails(shipment);

  const replacements: Record<string, string | number | null | undefined> = {
    '{{courier_tracking_id}}': shipment?.courier_tracking_id,
    '{{from_location.primary_contact_name}}': shipment?.from_location?.name,
    '{{battery_level}}': alert?.trigger?.currentValue,
    '{{battery_charge_at_origin_pct}}': alert?.trigger?.thresholdValue,
    '{{scheduled_pickup_date}}': shipment?.scheduled_pickup_date,
    '{{pickup_date}}':
      shipment?.actual_pickup_date || shipment?.scheduled_pickup_date,
    '{{sender_name}}': 'Mia',
    '{{sender_role}}': decodedToken?.user_role || 'Support',
    '{{sender_company}}': decodedToken?.company_name,
    '{{estimated_delivery_date}}': shipment?.estimated_delivery_date,
    '{{temp_range}}': shipment?.product?.temperature_range,
    '{{transport_provider.primary_contact_name}}': alert?.primary_contact?.name,
    '{{late_to_pickup_buffer_minutes}}': alert?.trigger?.thresholdValue,
    '{{flight_number}}': alert?.context?.flight_number,
    '{{flight_delay_buffer_minutes}}': alert?.trigger?.thresholdValue,
    '{{flight.source_airport_code}}': alert?.context?.source_airport_code,
    '{{flight.scheduled_on}}': alert?.context?.scheduled_on,
    '{{flight.estimated_off}}': alert?.context?.estimated_off,
    '{{airport_code}}': alert?.context?.airport_code,
    '{{slow_to_offload_buffer_minutes}}': alert?.trigger?.thresholdValue,
    '{{cant_find_location_buffer_minutes}}': alert?.trigger?.thresholdValue,
    '{{wrong_address_buffer_feet}}': alert?.trigger?.thresholdValue,
    '{{cargo_closing_hours}}': alert?.context?.cargo_closing_hours,
    '{{weather_event_severity}}': alert?.context?.weather_event_severity,
    '{{status_update_overdue_minutes}}': alert?.context?.overdue_duration,
    '{{not_moving_time_threshold_minutes}}': alert?.trigger?.thresholdValue,
    '{{current_temp}}': alert?.trigger?.currentValue,
    '{{temp_excursion_buffer_minutes}}': alert?.trigger?.thresholdValue,
    '{{remaining_viability_window}}':
      alert?.context?.remaining_viability_window,
    '{{estimated_transit_time_remaining}}':
      alert?.context?.estimated_transit_time_remaining,
    '{{viability_to_expiration_buffer_minutes}}':
      alert?.trigger?.thresholdValue,
    '{{viability_exhausted_buffer_minutes}}': alert?.trigger?.thresholdValue,
    '{{impact_time}}': alert?.trigger?.timestamp,
    '{{impact_level}}': alert?.trigger?.currentValue,
    '{{battery_charge_in_transit_pct}}': alert?.trigger?.thresholdValue,
    '{{tilt_threshold_deg}}': alert?.trigger?.thresholdValue,
    '{{tilt_angle_recorded}}': alert?.trigger?.currentValue,
    '{{pressue_threshold_psi}}': alert?.trigger?.thresholdValue,
    '{{humidity_threshold_pct}}': alert?.trigger?.thresholdValue,
    '{{shipment_details}}': shipmentDetails,
    '{{alertDetail}}': alert?.detail,
    '{{alert_name}}': alertName,
  };

  for (const placeholder in replacements) {
    const regex = new RegExp(
      placeholder.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'),
      'g'
    );
    const value = replacements[placeholder] ?? '';
    subject = subject.replace(regex, String(value));
    body = body.replace(regex, String(value));
    footer = footer.replace(regex, String(value));
  }

  return { body, subject, footer };
};

/**
 * Generates HTML preview for one-time email notifications
 *
 * @param shipment - Shipment information for template replacement
 * @param decodedToken - User token containing company information
 * @param customMessage - Optional custom message to include in email
 * @returns HTML formatted email body
 */
export const generateOneTimeEmailPreview = (
  shipment?: Shipment,
  decodedToken?: DecodedToken,
  customMessage?: string | null
): string => {
  const replacements: Record<string, string | undefined> = {
    '{{courier_tracking_id}}': shipment?.courier_tracking_id,
    '{{transport_provider.primary_contact_name}}':
      shipment?.tp_primary_contact?.name,
    '{{estimated_delivery_date}}': shipment?.estimated_delivery_date || 'soon',
    '{{shipment_details}}': getShipmentDetails(shipment),
    '{{contact_name}}': shipment?.tp_primary_contact?.name || 'there',
    '{{company_name}}': decodedToken?.company_name,
  };
  const defaultMessage = `
    Shipment ID {{courier_tracking_id}} is now expected to be delayed by 1hr or more, 
    and should arrive {{estimated_delivery_date}}. 
    Let us know if you have any questions.
  `;

  const message = customMessage || defaultMessage.trim();
  let body = `
    Hi {{contact_name}},
    <p style="margin-top: 10px; margin-bottom: 10px;">
      ${message}
   </p>
    <p style="margin-top: 15px; margin-bottom: 15px;">
      <strong>Mia</strong>, {{company_name}} logistics assistant
      </p>
<p style="margin-top: 15px; margin-bottom: 15px;">
    {{shipment_details}}
    </p>
  `;
  for (const placeholder in replacements) {
    const regex = new RegExp(
      placeholder.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'),
      'g'
    );
    const value = replacements[placeholder] ?? '';
    body = body.replace(regex, value);
  }

  return body;
};
